import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle, Handshake } from 'lucide-react';

const CATEGORIES = [
  { value: 'hebergement', label: 'Hébergement accessible' },
  { value: 'transport_pmr', label: 'Transport PMR' },
  { value: 'assistance_humaine', label: 'Assistance humaine' },
  { value: 'materiel', label: 'Matériel adapté' },
  { value: 'activites', label: 'Activités inclusives' },
  { value: 'autre', label: 'Autre' }
];

export default function PartnerApplicationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', category: '', contact_first_name: '', contact_last_name: '',
    contact_email: '', contact_phone: '', covered_zone: '', description: '',
    accessibility_level: '', availability: '', consent: false
  });

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error('Veuillez accepter les conditions'); return; }
    setLoading(true);
    try {
      await api.post('/partners/apply', form);
      navigate('/merci?type=partenaire');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 md:py-32 bg-background" data-testid="partner-application-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
            <Handshake className="w-8 h-8 text-secondary" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">Devenir partenaire</h1>
          <p className="text-muted-foreground">Rejoignez le réseau M-CareLink et contribuez à rendre les vacances accessibles.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] border border-border/50 space-y-5">
          <div>
            <Label>Raison sociale *</Label>
            <Input value={form.company_name} onChange={e => u('company_name', e.target.value)} required className="mt-1 rounded-xl" data-testid="partner-company" />
          </div>
          <div>
            <Label>Catégorie *</Label>
            <select value={form.category} onChange={e => u('category', e.target.value)} required className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="partner-category">
              <option value="">Sélectionnez votre catégorie...</option>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prénom du contact *</Label>
              <Input value={form.contact_first_name} onChange={e => u('contact_first_name', e.target.value)} required className="mt-1 rounded-xl" data-testid="partner-firstname" />
            </div>
            <div>
              <Label>Nom du contact *</Label>
              <Input value={form.contact_last_name} onChange={e => u('contact_last_name', e.target.value)} required className="mt-1 rounded-xl" data-testid="partner-lastname" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.contact_email} onChange={e => u('contact_email', e.target.value)} required className="mt-1 rounded-xl" data-testid="partner-email" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input type="tel" value={form.contact_phone} onChange={e => u('contact_phone', e.target.value)} className="mt-1 rounded-xl" data-testid="partner-phone" />
            </div>
          </div>
          <div>
            <Label>Zone couverte</Label>
            <Input value={form.covered_zone} onChange={e => u('covered_zone', e.target.value)} placeholder="Ex: Provence-Alpes-Côte d'Azur, National..." className="mt-1 rounded-xl" data-testid="partner-zone" />
          </div>
          <div>
            <Label>Description de votre activité</Label>
            <Textarea value={form.description} onChange={e => u('description', e.target.value)} placeholder="Décrivez votre activité et vos services..." className="mt-1 rounded-xl min-h-[100px]" data-testid="partner-description" />
          </div>
          <div>
            <Label>Niveau d'accessibilité</Label>
            <select value={form.accessibility_level} onChange={e => u('accessibility_level', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="partner-accessibility">
              <option value="">Sélectionnez...</option>
              <option value="Complet">Complet (toutes situations de handicap)</option>
              <option value="Partiel">Partiel (certaines situations)</option>
              <option value="En cours">En cours d'adaptation</option>
              <option value="Variable">Variable selon les prestations</option>
            </select>
          </div>
          <div>
            <Label>Disponibilité</Label>
            <Input value={form.availability} onChange={e => u('availability', e.target.value)} placeholder="Ex: Toute l'année, saison estivale..." className="mt-1 rounded-xl" data-testid="partner-availability" />
          </div>
          <div className="flex items-start gap-2 pt-2">
            <Checkbox id="consent" checked={form.consent} onCheckedChange={v => u('consent', v)} data-testid="partner-consent" />
            <Label htmlFor="consent" className="text-sm cursor-pointer leading-relaxed">
              J'accepte que mes données soient traitées par M-CareLink dans le cadre de ma candidature partenaire.
            </Label>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-secondary text-secondary-foreground py-5 font-semibold hover:bg-secondary/90 transition-all hover:scale-[1.02]" data-testid="partner-submit-btn">
            {loading ? 'Envoi...' : 'Soumettre ma candidature'} <CheckCircle className="ml-2 w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
